package diana.soleil.design.iraniancanadienbazzar.feature.admin

import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.AdminRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignChannel
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignSendResult
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_all_canada
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_audience
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_body_en
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_body_fa
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_body_fr
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_channel
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_channel_email
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_channel_push
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_email_note
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_business
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_event
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_id
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_item
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_post
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_dest_url
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_capped
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_failed
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_target_missing
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_hint
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_send
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_sending
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_sent
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_title_en
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_title_fa
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_camp_title_fr
import kotlin.random.Random
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

/** Send progress of the one-shot campaign action. */
data class CampaignUiState(
    val sending: Boolean = false,
    /** Recipient count of the last successful send (null = nothing sent yet this session). */
    val sentCount: Int? = null,
    /** True when the server truncated the audience at its recipient cap — the admin must know. */
    val capped: Boolean = false,
    val failed: Boolean = false,
    /** The deep link's event/business/post/item does not exist (server `not-found`). */
    val targetMissing: Boolean = false,
)

/** Drives the admin campaign composer — thin wrapper over the `sendCampaign` callable. */
class CampaignViewModel(
    private val admin: AdminRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(CampaignUiState())
    val state: StateFlow<CampaignUiState> = _state.asStateFlow()

    /**
     * Idempotency key: minted for the first attempt, REUSED on retry-after-failure (a timed-out
     * callable usually finished server-side — the same id makes the retry a no-op instead of a
     * double-send to the whole audience), and cleared once a send succeeds.
     */
    private var pendingCampaignId: String? = null

    private fun mintCampaignId(): String =
        List(4) { Random.nextInt(0x10000).toString(16).padStart(4, '0') }.joinToString("")

    fun send(
        metroId: String?,
        title: LocalizedText,
        body: LocalizedText,
        link: String,
        channel: CampaignChannel,
    ) {
        if (_state.value.sending) return
        val campaignId = pendingCampaignId ?: mintCampaignId().also { pendingCampaignId = it }
        // Reset synchronously so the previous send's result line can never linger while the new
        // one is in flight (cold-start callables can take >10s — observed in the on-device E2E).
        _state.value = CampaignUiState(sending = true)
        viewModelScope.launch {
            _state.value = when (val result = admin.sendCampaign(campaignId, metroId, title, body, link, channel)) {
                is DataResult.Success -> {
                    pendingCampaignId = null
                    CampaignUiState(sentCount = result.data.sent, capped = result.data.capped)
                }
                is DataResult.Failure -> CampaignUiState(
                    failed = true,
                    targetMissing = result.error is AppError.NotFound,
                )
            }
        }
    }
}

/** What a campaign's deep link opens; Url = a raw pasted universal link. */
private enum class CampaignDest { Event, Business, Post, Item, Url }

/**
 * Admin campaign composer (docs/CAMPAIGNS_AND_QR.md): audience (all Canada / one metro) + EN
 * title/body (FR/FA optional — the server localizes per recipient with EN fallback) + a deep-link
 * destination (type + id builds the universal link, or paste a URL). Send fans out push + bell
 * notifications server-side; the callable re-checks the admin role, so this UI is convenience,
 * not the security boundary.
 */
@Composable
fun CampaignComposerCard(
    modifier: Modifier = Modifier,
    viewModel: CampaignViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val shareConfig = koinInject<ShareConfig>()
    val language = AppTheme.language

    var metroId by remember { mutableStateOf<String?>(null) }
    var titleEn by remember { mutableStateOf("") }
    var titleFr by remember { mutableStateOf("") }
    var titleFa by remember { mutableStateOf("") }
    var bodyEn by remember { mutableStateOf("") }
    var bodyFr by remember { mutableStateOf("") }
    var bodyFa by remember { mutableStateOf("") }
    var channel by remember { mutableStateOf(CampaignChannel.Push) }
    var dest by remember { mutableStateOf(CampaignDest.Event) }
    var destValue by remember { mutableStateOf("") }

    val link = when (dest) {
        CampaignDest.Event -> shareConfig.eventLink(destValue.trim())
        CampaignDest.Business -> shareConfig.businessLink(destValue.trim())
        CampaignDest.Post -> shareConfig.postLink(destValue.trim())
        CampaignDest.Item -> shareConfig.itemLink(destValue.trim())
        CampaignDest.Url -> destValue.trim()
    }
    val linkValid = destValue.isNotBlank() &&
        (dest != CampaignDest.Url || link.startsWith("https://"))

    AppCard(modifier = modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            Text(
                text = stringResource(Res.string.admin_camp_hint),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
            )

            // Audience
            Text(stringResource(Res.string.admin_camp_audience), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                AppChip(
                    label = stringResource(Res.string.admin_camp_all_canada),
                    selected = metroId == null,
                    onClick = { metroId = null },
                )
                Metros.all.forEach { metro ->
                    AppChip(
                        label = metro.name.resolve(language),
                        selected = metroId == metro.id,
                        onClick = { metroId = metro.id },
                    )
                }
            }

            // Channel — push (default) or CASL-gated email (server enforces consent + suppression).
            Text(stringResource(Res.string.admin_camp_channel), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppChip(
                    label = stringResource(Res.string.admin_camp_channel_push),
                    selected = channel == CampaignChannel.Push,
                    onClick = { channel = CampaignChannel.Push },
                )
                AppChip(
                    label = stringResource(Res.string.admin_camp_channel_email),
                    selected = channel == CampaignChannel.Email,
                    onClick = { channel = CampaignChannel.Email },
                )
            }
            if (channel == CampaignChannel.Email) {
                Text(
                    text = stringResource(Res.string.admin_camp_email_note),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }

            // Copy (EN required; FR/FA optional — server falls back to EN per recipient)
            AppTextField(value = titleEn, onValueChange = { titleEn = it }, label = stringResource(Res.string.admin_camp_title_en), modifier = Modifier.fillMaxWidth())
            AppTextField(value = bodyEn, onValueChange = { bodyEn = it }, label = stringResource(Res.string.admin_camp_body_en), modifier = Modifier.fillMaxWidth())
            AppTextField(value = titleFr, onValueChange = { titleFr = it }, label = stringResource(Res.string.admin_camp_title_fr), modifier = Modifier.fillMaxWidth())
            AppTextField(value = bodyFr, onValueChange = { bodyFr = it }, label = stringResource(Res.string.admin_camp_body_fr), modifier = Modifier.fillMaxWidth())
            AppTextField(value = titleFa, onValueChange = { titleFa = it }, label = stringResource(Res.string.admin_camp_title_fa), modifier = Modifier.fillMaxWidth())
            AppTextField(value = bodyFa, onValueChange = { bodyFa = it }, label = stringResource(Res.string.admin_camp_body_fa), modifier = Modifier.fillMaxWidth())

            // Destination
            Text(stringResource(Res.string.admin_camp_dest), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                AppChip(label = stringResource(Res.string.admin_camp_dest_event), selected = dest == CampaignDest.Event, onClick = { dest = CampaignDest.Event })
                AppChip(label = stringResource(Res.string.admin_camp_dest_business), selected = dest == CampaignDest.Business, onClick = { dest = CampaignDest.Business })
                AppChip(label = stringResource(Res.string.admin_camp_dest_post), selected = dest == CampaignDest.Post, onClick = { dest = CampaignDest.Post })
                AppChip(label = stringResource(Res.string.admin_camp_dest_item), selected = dest == CampaignDest.Item, onClick = { dest = CampaignDest.Item })
                AppChip(label = stringResource(Res.string.admin_camp_dest_url), selected = dest == CampaignDest.Url, onClick = { dest = CampaignDest.Url })
            }
            AppTextField(
                value = destValue,
                onValueChange = { destValue = it },
                label = stringResource(if (dest == CampaignDest.Url) Res.string.admin_camp_dest_url else Res.string.admin_camp_dest_id),
                supportingText = if (linkValid) link else null,
                modifier = Modifier.fillMaxWidth(),
            )

            val sent = state.sentCount
            if (sent != null) {
                Text(
                    text = stringResource(Res.string.admin_camp_sent, sent),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.success,
                )
                if (state.capped) {
                    Text(
                        text = stringResource(Res.string.admin_camp_capped),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.error,
                    )
                }
            }
            if (state.failed) {
                Text(
                    text = stringResource(
                        if (state.targetMissing) Res.string.admin_camp_target_missing else Res.string.admin_camp_failed,
                    ),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.error,
                )
            }
            AppButton(
                text = stringResource(if (state.sending) Res.string.admin_camp_sending else Res.string.admin_camp_send),
                onClick = {
                    viewModel.send(
                        metroId = metroId,
                        title = LocalizedText(en = titleEn.trim(), fr = titleFr.trim().ifBlank { null }, fa = titleFa.trim().ifBlank { null }),
                        body = LocalizedText(en = bodyEn.trim().ifBlank { null }, fr = bodyFr.trim().ifBlank { null }, fa = bodyFa.trim().ifBlank { null }),
                        link = link,
                        channel = channel,
                    )
                },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = "campaign",
                enabled = titleEn.isNotBlank() && linkValid && !state.sending,
            )
        }
    }
}
